import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type Address } from '~/types/address';
import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { type Coordinates } from '~/models/masterdata/Coordinates';
import { type ParentOrganisationalGroup } from '~/models/masterdata/OrganisationalGroup';
import { SiteObject } from '~/models/masterdata/Site';

import { queryKeysSite } from './queryKeys';

type SiteLegacy = {
  address: Address;
  id: UUID;
  name: string;
  type: string;
  isActive: boolean;
  isSynced?: boolean;
  start: string;
  end: string;
  syncKey: string;
  companyId: UUID;
  companyAccountId: UUID;
  accountingReferenceList: UUID[];
  identifierList: UUID[];
  orgUnits: UUID[];
  orgUnitPaths: UUID[][];
  parentOrganizationalUnits: ParentOrganisationalGroup[];
  parentOrganizationalUnitPaths: UUID[];
  coords?: Coordinates;
};

type SitesLegacyResponse = {
  items: readonly SiteLegacy[];
  count: number;
};

/**
 * Fetches sites legacy data from the API.
 * @returns {Promise<SiteLegacy[]>} The site data if successful, null otherwise.
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/get_all_sites_site_all_get
 */

export const fetchSitesLegacy = async () => {
  const { items } = await vestigasApi
    .get(ENDPOINT.SITE.GET_ALL_LEGACY())
    .json<SitesLegacyResponse>();

  return items.map((item) => SiteObject.create(item));
};

// This query uses an deprecated endpoint for fetching ALL sites ever, which is not technically possible for large accounts.
export const useQuerySitesLegacy = (
  options?: Omit<UseQueryOptions, 'queryKey' | 'queryFn'>,
) =>
  useQuery({
    queryFn: async () => fetchSitesLegacy(),
    queryKey: queryKeysSite.getAllLegacy(),
    ...options,
  });
